import { storage, type OpenTab } from '../services/storage';

export type TabCallback = (tabId: string) => void;
export type TabCloseCallback = (tabId: string) => boolean; // return false to prevent close
export type TabClosedCallback = () => void; // called after tab is closed
export type TabRenameCallback = (tabId: string, newTitle: string) => void;

export class TabManager {
  private container: HTMLElement;
  private tabs: OpenTab[] = [];
  private activeTabId: string | null = null;
  private workspaceId: string;
  private onSelect: TabCallback | null = null;
  private onClose: TabCloseCallback | null = null;
  private onClosed: TabClosedCallback | null = null;
  private onRename: TabRenameCallback | null = null;
  private onInfoClick: TabCallback | null = null;

  constructor(containerId: string, workspaceId?: string) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Container ${containerId} not found`);
    }
    this.container = container;
    this.workspaceId = workspaceId || storage.getActiveWorkspaceId();
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    this.tabs = storage.getOpenTabs(this.workspaceId);
    this.activeTabId = storage.getActiveTabId(this.workspaceId);
  }

  private saveToStorage(): void {
    storage.setOpenTabs(this.tabs, this.workspaceId);
    if (this.activeTabId) {
      storage.setActiveTabId(this.activeTabId, this.workspaceId);
    } else {
      storage.clearActiveTabId(this.workspaceId);
    }
  }

  // Switch to a different workspace - preserves tabs per workspace
  setWorkspace(workspaceId: string): void {
    if (this.workspaceId === workspaceId) return;

    // Save current workspace tabs
    this.saveToStorage();

    // Switch workspace
    this.workspaceId = workspaceId;
    storage.setActiveWorkspaceId(workspaceId);

    // Load new workspace tabs
    this.loadFromStorage();
    this.render();
  }

  getWorkspaceId(): string {
    return this.workspaceId;
  }

  render(): void {
    this.container.innerHTML = '';

    for (const tab of this.tabs) {
      const tabEl = document.createElement('button');
      const saveClass = tab.hasUnsavedChanges ? ' tab-unsaved' : ' tab-saved';
      tabEl.className = `tab${tab.id === this.activeTabId ? ' active' : ''}${saveClass}`;
      tabEl.dataset.id = tab.id;

      // Status indicator dot
      const statusDot = document.createElement('span');
      statusDot.className = 'tab-status';
      statusDot.title = tab.hasUnsavedChanges ? 'Unsaved changes' : 'Saved';
      tabEl.appendChild(statusDot);

      const titleSpan = document.createElement('span');
      titleSpan.className = 'tab-title';
      titleSpan.textContent = tab.title || 'Sin título';
      titleSpan.title = tab.title || 'Sin título'; // Tooltip with full name

      // Doble clic para renombrar
      titleSpan.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        const newTitle = prompt('Nuevo nombre:', tab.title);
        if (newTitle && newTitle.trim() && newTitle !== tab.title) {
          if (this.onRename) {
            this.onRename(tab.id, newTitle.trim());
          }
        }
      });

      tabEl.appendChild(titleSpan);

      // Info icon
      const infoBtn = document.createElement('span');
      infoBtn.className = 'tab-info';
      infoBtn.innerHTML = '<span class="material-symbols-outlined">info</span>';
      infoBtn.title = 'Document info';
      infoBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.onInfoClick) {
          this.onInfoClick(tab.id);
        }
      });
      tabEl.appendChild(infoBtn);

      const closeBtn = document.createElement('button');
      closeBtn.className = 'tab-close';
      closeBtn.innerHTML = '<span class="material-symbols-outlined">close</span>';
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeTab(tab.id);
      });
      tabEl.appendChild(closeBtn);

      tabEl.addEventListener('click', () => {
        this.selectTab(tab.id);
      });

      this.container.appendChild(tabEl);
    }
  }

  openTab(id: string, title: string): void {
    const existing = this.tabs.find(t => t.id === id);

    if (!existing) {
      this.tabs.push({
        id,
        title,
        hasUnsavedChanges: false
      });
    }

    this.activeTabId = id;
    this.saveToStorage();
    this.render();

    if (this.onSelect) {
      this.onSelect(id);
    }
  }

  selectTab(id: string): void {
    const wasAlreadyActive = this.activeTabId === id;

    this.activeTabId = id;

    if (!wasAlreadyActive) {
      this.saveToStorage();
      this.render();
    }

    // Always call onSelect to ensure view switches even if tab was already active
    if (this.onSelect) {
      this.onSelect(id);
    }
  }

  closeTab(id: string, force: boolean = false): void {
    const tab = this.tabs.find(t => t.id === id);
    if (!tab) return;

    // Preguntar si hay cambios sin guardar (a menos que force=true)
    if (!force && this.onClose && !this.onClose(id)) {
      return;
    }

    const index = this.tabs.findIndex(t => t.id === id);
    this.tabs = this.tabs.filter(t => t.id !== id);
    storage.removeOpenTab(id, this.workspaceId);

    // Seleccionar otra pestaña si cerramos la activa
    if (this.activeTabId === id) {
      if (this.tabs.length > 0) {
        const newIndex = Math.min(index, this.tabs.length - 1);
        this.activeTabId = this.tabs[newIndex].id;

        if (this.onSelect) {
          this.onSelect(this.activeTabId);
        }
      } else {
        this.activeTabId = null;
      }
    }

    this.saveToStorage();
    this.render();

    // Notificar que se cerró un tab
    if (this.onClosed) {
      this.onClosed();
    }
  }

  updateTabTitle(id: string, title: string): void {
    const tab = this.tabs.find(t => t.id === id);
    if (tab) {
      tab.title = title;
      this.saveToStorage();
      this.render();
    }
  }

  setUnsaved(id: string, unsaved: boolean): void {
    const tab = this.tabs.find(t => t.id === id);
    if (tab) {
      tab.hasUnsavedChanges = unsaved;
      storage.updateTabUnsaved(id, unsaved, this.workspaceId);
      this.render();
    }
  }

  onTabSelect(callback: TabCallback): void {
    this.onSelect = callback;
  }

  onTabClose(callback: TabCloseCallback): void {
    this.onClose = callback;
  }

  onTabClosed(callback: TabClosedCallback): void {
    this.onClosed = callback;
  }

  onTabRename(callback: TabRenameCallback): void {
    this.onRename = callback;
  }

  onTabInfo(callback: TabCallback): void {
    this.onInfoClick = callback;
  }

  getActiveTabId(): string | null {
    return this.activeTabId;
  }

  getTabs(): OpenTab[] {
    return this.tabs;
  }

  hasTab(id: string): boolean {
    return this.tabs.some(t => t.id === id);
  }

  hasUnsavedChanges(): boolean {
    return this.tabs.some(t => t.hasUnsavedChanges);
  }
}
